/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { jsx } from '@emotion/react';
import type { Interpolation, Theme } from '@emotion/react';

import * as React from 'react';

import { FormattedMessage } from 'js/lib/coursera.react-intl';
import { useLocation } from 'js/lib/useRouter';

import { Typography2 } from '@coursera/cds-core';

import LinkButton from 'bundles/authentication-common/components/LinkButton';
import SSOEnforcementBanner from 'bundles/authentication-common/components/SSOEnforcementBanner';
import {
  encodeSsoSamlMagicLink,
  getThirdPartyOrganizationSsoLoginUrl,
} from 'bundles/authentication-common/components/actions';
import type { InvalidDomainSSOError } from 'bundles/authentication-common/components/sso/SSOError';
import { AUTHENTICATION_MODAL_MODES, SSO_BANNER_NAMES } from 'bundles/authentication-common/constants';
import type { AUTHENTICATION_MODAL_MODES_KEY } from 'bundles/authentication-common/constants';

import _t from 'i18n!nls/authentication';

type InvalidDomainSSOEnforcementBannerProps = {
  ssoError: InvalidDomainSSOError;
  navigate?: (
    path: AUTHENTICATION_MODAL_MODES_KEY,
    options?: $TSFixMe,
    shouldSwitchFormsByQueryParam?: boolean
  ) => void;
  css?: Interpolation<Theme>;
};

const InvalidDomainSSOEnforcementBanner = ({
  ssoError,
  navigate,
  css: customCss,
}: InvalidDomainSSOEnforcementBannerProps) => {
  const [isResending, setIsResending] = React.useState(false);
  const redirectUrlRef = React.useRef<string | undefined>(undefined);
  const location = useLocation();
  const returnTo = (location.query ?? {}).redirectTo;

  React.useEffect(() => {
    const organizationId = ssoError.ssoLoginResponse?.thirdPartyOrganizationId;
    redirectUrlRef.current = undefined;

    if (organizationId) {
      (async () => {
        try {
          redirectUrlRef.current = await getThirdPartyOrganizationSsoLoginUrl(organizationId, returnTo || '/');
        } catch {
          redirectUrlRef.current = undefined;
        }
      })();
    }
  }, [ssoError.ssoLoginResponse?.thirdPartyOrganizationId, returnTo]);

  const resendMagicLink = async () => {
    const ssoErrorTokenExists = !!ssoError?.token;
    if (ssoErrorTokenExists && !isResending) {
      setIsResending(true);
      try {
        const result = await encodeSsoSamlMagicLink({
          samlLoginId: ssoError?.token || '',
        });
        if (result.success) {
          navigate?.(AUTHENTICATION_MODAL_MODES.MAGICLINK, { email: ssoError.ssoLoginResponse?.email });
        } else if (result.shouldRedirectToIdP && redirectUrlRef.current) {
          window.location.replace(redirectUrlRef.current);
        }
      } finally {
        setIsResending(false);
      }
    }
  };

  if (ssoError.showResendLink) {
    return (
      <SSOEnforcementBanner severity="information" bannerName={SSO_BANNER_NAMES.INVALID_DOMAIN} css={customCss}>
        <>
          <Typography2 variant="subtitleMedium" component="span">
            {_t('A sign-in link is on its way')}
          </Typography2>
          <br />
          <FormattedMessage
            message={_t(
              "SSO didn't work for this email, so we've sent you a secure link instead. Check your inbox to continue. Didn’t get? {resendLink}"
            )}
            resendLink={
              <LinkButton disabled={isResending} onClick={resendMagicLink}>
                {_t('Resend link')}
              </LinkButton>
            }
          />
        </>
      </SSOEnforcementBanner>
    );
  } else if (ssoError.isSSOLockedOut) {
    return (
      <SSOEnforcementBanner bannerName={SSO_BANNER_NAMES.INVALID_DOMAIN} css={customCss}>
        {_t(
          "You can't sign in. Domain verification failed. Your organization requires mandatory SSO. Contact your Admin for access."
        )}
      </SSOEnforcementBanner>
    );
  } else if (ssoError.ssoLoginResponse?.authMode === 'login') {
    return (
      <SSOEnforcementBanner severity="information" bannerName={SSO_BANNER_NAMES.INVALID_DOMAIN} css={customCss}>
        <>
          <Typography2 variant="subtitleMedium" component="span">
            {_t('One more step to get you in')}
          </Typography2>
          <br />
          {_t("SSO didn't work for this email. Login in with a link, your password, or a social account below.")}
        </>
      </SSOEnforcementBanner>
    );
  } else if (ssoError.ssoLoginResponse?.authMode === 'signup') {
    return (
      <SSOEnforcementBanner severity="information" bannerName={SSO_BANNER_NAMES.INVALID_DOMAIN} css={customCss}>
        <>
          <Typography2 variant="subtitleMedium" component="span">
            {_t('One more step to get you in')}
          </Typography2>
          <br />
          {_t("SSO didn't work for this email. Set a password or continue with a social account below.")}
        </>
      </SSOEnforcementBanner>
    );
  } else {
    return (
      <SSOEnforcementBanner bannerName={SSO_BANNER_NAMES.INVALID_DOMAIN} css={customCss}>
        {_t(
          "We couldn't verify your email domain. Login or create an account using another login method. Contact your admin if the issue persists."
        )}
      </SSOEnforcementBanner>
    );
  }
};

export default InvalidDomainSSOEnforcementBanner;
